/**
 * PerCellGenerateDialog (Phase 3.2) — full multi-mode per-cell generator.
 *
 * Modes (mutually exclusive):
 *   - single           — 1 derivative for this cell.
 *   - variants         — N derivatives, one per template selected in
 *                        Templates tab; only enabled when >=2 templates.
 *   - fresh-variants   — N derivatives sharing one template, distinct
 *                        AI seeds. Slider 2-5.
 *   - series           — N narrative-sequence parts using ONE template
 *                        (templateIdsByPlatform[platform][0] or AI default).
 *                        Slider 2-10 + flavor select.
 *
 * Method (cope/direct) is independent. Heuristic default mirrors the
 * existing decidePostGenerationMode logic on the client side.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, RadioGroup, FormControlLabel, Radio,
  ToggleButton, ToggleButtonGroup, TextField, FormControl, Select, MenuItem,
  InputLabel, Switch, Chip, Alert, Slider, Button,
} from '@mui/material';
import { SDialog } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { useAuthor } from '../../hooks/useAuthor';
import { usePerAuthorPostTemplates } from '../../hooks/usePerAuthorPostTemplates';
import { usePerAuthorPostFormats } from '../../hooks/usePerAuthorPostFormats';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import { useTopicResearchRuns } from '../../hooks/useTopicResearchRuns';
import type { ContentTopic, PlatformId, PublicationSlotAssignment } from '../../types';
import {
  isTemplateCompatibleWithFormat,
  pickBestCompatibleFormatForTemplate,
} from '@modules/content-plan/utils/postTemplateFormatCompatibility';
import {
  ResearchScopeSelector,
  buildResearchSelection,
  createResearchScopeState,
  type ResearchScopeSelectorState,
} from '../research/ResearchScopeSelector';

type AssetMode = 'plan_and_generate' | 'plan_only' | 'text_only';
type CellMode = 'single' | 'variants' | 'series' | 'fresh-variants';
type SeriesFlavor = 'continuation' | 'counter_point' | 'how_to_sequence' | 'story_arc';
type PlanningMode = 'auto' | 'manual';
type PostGoal = 'branding' | 'leadgen' | 'engagement' | 'education' | 'recruiting' | 'launch';
type AssetIntent = 'text_only' | 'hero_image' | 'carousel' | 'video_cover' | 'native_document' | 'auto';

const POST_GOALS: PostGoal[] = ['branding', 'leadgen', 'engagement', 'education', 'recruiting', 'launch'];
const ASSET_INTENTS: AssetIntent[] = ['auto', 'text_only', 'hero_image', 'carousel', 'video_cover', 'native_document'];
const SLOT_INSTRUCTION_MAX = 500;

export interface PerCellGenerateDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  authorId: string;
  topic: ContentTopic;
  aspectKey: string | null;
  platform: PlatformId;
  /** Row-scoped master generation fan-out. Omit for derivative-cell generation. */
  rowPlatforms?: PlatformId[];
  target?: 'master' | 'derivative';
  strategyId?: string;
  sprintId?: string;
  publicationId?: string;
  slotLocked?: boolean;
  slotAssignment?: PublicationSlotAssignment | null;
  /**
   * Fires when submission starts (`busy=true`) and ends (`busy=false`).
   * On the start call, `taskId` is undefined (no API result yet); on the
   * end call, `taskId` is the backend `backend_tasks/{id}` reference returned
   * by the API — used by `PostsGrid` to show a live `<CellTaskBadge>` over
   * the empty cell while the post is being generated (before the post-doc
   * with `lastTaskId` field exists in Firestore).
   */
  onSubmitting?: (busy: boolean, taskId?: string) => void;
}

/**
 * Client-side mirror of the backend `decidePostGenerationMode` predicate.
 * Used as the radio default so the user sees a sensible pre-selection but
 * can override (Section 0.3 — explicit user choice wins).
 */
function defaultMethod(
  topic: ContentTopic,
  platform: PlatformId,
): 'cope' | 'direct' {
  const formatHints = Array.isArray((topic as any).formatHint)
    ? ((topic as any).formatHint as string[])
    : [(topic as any).formatHint].filter(Boolean) as string[];
  if (formatHints.some((hint) => ['thread', 'carousel', 'reel', 'story'].includes(hint))) {
    return 'direct';
  }
  const targetPlatforms = ((topic as any).targetPlatforms ?? []) as string[];
  if (targetPlatforms.length === 1 && targetPlatforms[0] === platform) {
    return 'direct';
  }
  return 'cope';
}

export const PerCellGenerateDialog: React.FC<PerCellGenerateDialogProps> = ({
  open, onClose, projectId, userId, topic,
  authorId, aspectKey, platform, rowPlatforms, target = 'derivative', strategyId, sprintId, publicationId, slotLocked = false, slotAssignment, onSubmitting,
}) => {
  const { author } = useAuthor(projectId);
  const { templates: platformTemplates } = usePerAuthorPostTemplates(
    projectId, authorId || author?.id || null, platform,
  );
  const { formats: platformFormats } = usePerAuthorPostFormats(
    projectId, authorId || author?.id || null, platform,
  );
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const { runs: topicRuns, loading: runsLoading } = useTopicResearchRuns(
    open ? projectId : null,
    open ? topic.id : null,
    authorId,
  );

  const [method, setMethod] = useState<'cope' | 'direct'>(
    () => defaultMethod(topic, platform),
  );
  const [planningMode, setPlanningMode] = useState<PlanningMode>('auto');
  const [assignedGoal, setAssignedGoal] = useState<PostGoal>('branding');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedFormatId, setSelectedFormatId] = useState('');
  const [assetIntent, setAssetIntent] = useState<AssetIntent>('auto');
  const [lengthBucket, setLengthBucket] = useState<'short' | 'medium' | 'long'>('medium');
  const [cellMode, setCellMode] = useState<CellMode>('single');
  const [assetMode, setAssetMode] = useState<AssetMode>('plan_and_generate');
  const [assetModeUserTouched, setAssetModeUserTouched] = useState(false);
  const [userInstruction, setUserInstruction] = useState('');
  const [draftReview, setDraftReview] = useState(false);
  const [seriesCount, setSeriesCount] = useState(3);
  const [seriesFlavor, setSeriesFlavor] = useState<SeriesFlavor>('continuation');
  const [freshCount, setFreshCount] = useState(2);
  // Model selection — initial value comes from project default (lazily,
  // so a freshly-set default propagates the next time the dialog opens).
  const [model, setModel] = useState<string>(projectDefaultModel.value);
  const [savingDefault, setSavingDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [researchScope, setResearchScope] = useState<ResearchScopeSelectorState>(
    () => createResearchScopeState([]),
  );

  // Reset transient state on open so re-opening the dialog doesn't carry
  // last submission's slider / flavor values into the next cell.
  useEffect(() => {
    if (open) {
      setMethod(defaultMethod(topic, platform));
      setPlanningMode('auto');
      setAssignedGoal('branding');
      setSelectedTemplateId('');
      setSelectedFormatId('');
      setAssetIntent('auto');
      setLengthBucket('medium');
      setCellMode('single');
      setAssetMode('plan_and_generate');
      setAssetModeUserTouched(false);
      setUserInstruction('');
      setDraftReview(false);
      setSeriesCount(3);
      setSeriesFlavor('continuation');
      setFreshCount(2);
      setModel(projectDefaultModel.value);
      setError(null);
      setResearchScope(createResearchScopeState([]));
    }
  }, [open, topic, platform, projectDefaultModel.value]);

  // v1.8.0: pre-fill the assetMode default from the backend env-resolver
  // (CONTENT_PLAN_DEFAULT_ASSET_MODE). Skipped when the user already touched
  // the dropdown so a manual choice is never overridden.
  useEffect(() => {
    if (!open || assetModeUserTouched) return;
    let cancelled = false;
    (async () => {
      try {
        const client = new ContentPlanApiClient();
        const r = await client.getAssetModeDefault();
        if (cancelled || assetModeUserTouched) return;
        if (r?.mode && r.mode !== assetMode) setAssetMode(r.mode as AssetMode);
      } catch {
        // Silent fallback — keep the hardcoded default.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || runsLoading) return;
    setResearchScope((prev) => prev.touched ? prev : createResearchScopeState(topicRuns));
  }, [open, runsLoading, topicRuns]);

  const aspectLabel = useMemo(() => {
    if (!aspectKey) return null;
    const breakdown = ((topic as any).postBreakdown ?? []) as Array<{
      aspectKey: string; aspectLabel: string;
    }>;
    return breakdown.find((b) => b.aspectKey === aspectKey)?.aspectLabel ?? aspectKey;
  }, [aspectKey, topic]);

  const normalizedRowPlatforms = useMemo(
    () => Array.from(new Set((rowPlatforms ?? [platform])
      .map((item) => String(item).trim())
      .filter(Boolean))) as PlatformId[],
    [platform, rowPlatforms],
  );
  const isSlotLocked = Boolean(slotLocked || publicationId);
  const isMasterRowGeneration = target === 'master';
  const targetPlatforms = isSlotLocked
    ? [platform]
    : (isMasterRowGeneration ? normalizedRowPlatforms : [platform]);
  const titleSuffix = aspectLabel
    ? `${aspectLabel} × ${isMasterRowGeneration ? targetPlatforms.join(', ') : platform}`
    : (isMasterRowGeneration ? targetPlatforms.join(', ') : platform);

  // ── Templates available for this cell ──────────────────────────
  // Source of truth: topic.templateIdsByPlatform[platform] (whitelist
  // configured via Templates tab). Falls back to the platform's library
  // first item when topic-level whitelist is empty.
  const topicTemplateIds = useMemo(() => {
    const map = topic.templateIdsByPlatform ?? {};
    return map[platform] ?? [];
  }, [topic, platform]);

  const availableTemplates = useMemo(() => {
    if (topicTemplateIds.length > 0) {
      return platformTemplates.filter((t) => topicTemplateIds.includes(t.id));
    }
    return platformTemplates;
  }, [platformTemplates, topicTemplateIds]);
  const selectedTemplate = useMemo(
    () => availableTemplates.find((template) => template.id === selectedTemplateId) ?? null,
    [availableTemplates, selectedTemplateId],
  );
  const selectedFormat = useMemo(
    () => platformFormats.find((format) => format.id === selectedFormatId) ?? null,
    [platformFormats, selectedFormatId],
  );
  const templatesCompatibleWithSelectedFormat = useMemo(
    () => selectedFormat
      ? availableTemplates.filter((template) => isTemplateCompatibleWithFormat(template, selectedFormat))
      : availableTemplates,
    [availableTemplates, selectedFormat],
  );
  const selectedFormatCompatible = Boolean(
    selectedFormat
      && (!selectedTemplate || isTemplateCompatibleWithFormat(selectedTemplate, selectedFormat)),
  );

  const variantsEligible = topicTemplateIds.length >= 2;

  const pickTemplateForGoal = (
    goal: PostGoal,
    format = selectedFormat,
    requireCompatible = false,
  ) => {
    const pool = format
      ? availableTemplates.filter((template) => isTemplateCompatibleWithFormat(template, format))
      : availableTemplates;
    return pool.find((template) => template.goal === goal)
      ?? pool[0]
      ?? (requireCompatible ? null : availableTemplates.find((template) => template.goal === goal))
      ?? (requireCompatible ? null : availableTemplates[0])
      ?? null;
  };

  const applyTemplateDefaults = (templateId: string) => {
    const template = availableTemplates.find((item) => item.id === templateId);
    if (!template) return;
    if (template.goal && POST_GOALS.includes(template.goal as PostGoal)) {
      setAssignedGoal(template.goal as PostGoal);
    }
    if (
      template.lengthBucket
      && ['short', 'medium', 'long'].includes(template.lengthBucket)
    ) {
      setLengthBucket(template.lengthBucket as 'short' | 'medium' | 'long');
    }
  };

  useEffect(() => {
    if (!open) return;
    const selectedMatchesGoal = selectedTemplate?.goal === assignedGoal;
    if (
      (!selectedTemplate || !selectedMatchesGoal || (selectedFormat && !selectedFormatCompatible))
      && availableTemplates.length > 0
    ) {
      const next = pickTemplateForGoal(assignedGoal, selectedFormat, Boolean(selectedFormat));
      if (next?.id) {
        setSelectedTemplateId(next.id);
        applyTemplateDefaults(next.id);
      }
    }
  }, [
    open,
    planningMode,
    assignedGoal,
    selectedTemplate,
    selectedFormat,
    selectedFormatCompatible,
    availableTemplates,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!selectedFormatId) {
      const nextFormat = selectedTemplate
        ? pickBestCompatibleFormatForTemplate(selectedTemplate, platformFormats)
        : platformFormats[0] ?? null;
      if (nextFormat?.id) setSelectedFormatId(String(nextFormat.id));
    }
  }, [open, selectedFormatId, selectedTemplate, platformFormats]);

  const manualPlanComplete = planningMode === 'auto'
    || Boolean(
      method
      && assignedGoal
      && selectedTemplateId
      && selectedFormatId
      && assetIntent
      && selectedFormatCompatible,
    );
  const canSubmit = isSlotLocked || manualPlanComplete;

  // ── Submission ─────────────────────────────────────────────────
  const submit = async () => {
    setError(null);
    setSubmitting(true);
    onSubmitting?.(true);
    try {
      const client = new ContentPlanApiClient();
      const effectivePlanningMode: PlanningMode = isSlotLocked ? 'auto' : planningMode;
      const effectiveCellMode: CellMode = isSlotLocked ? 'single' : cellMode;
      const applyMode = isSlotLocked
        ? 'auto_apply_sprint'
        : draftReview
        ? 'draft_review'
        : (sprintId ? 'auto_apply_sprint' : 'auto_apply_topic');

      // templateIds rules per cellMode:
      //   - single:         [topicTemplateIds[0]] (or empty → AI default)
      //   - variants:       full topicTemplateIds list (one variant per template)
      //   - series:         [topicTemplateIds[0]] (one template, N parts)
      //   - fresh-variants: [topicTemplateIds[0]] (same template, N seeds)
      let templateIds: string[] | undefined;
      if (isSlotLocked) {
        templateIds = undefined;
      } else if (planningMode === 'manual') {
        templateIds = selectedTemplateId ? [selectedTemplateId] : undefined;
      } else if (cellMode === 'variants') {
        templateIds = topicTemplateIds.length > 0 ? topicTemplateIds : undefined;
      } else {
        templateIds = undefined;
      }

      const cellScopePayload: Parameters<typeof client.generatePosts>[0]['cellScope'] = {
        aspectKey,
        platform: platform as string,
        ...(!isSlotLocked && isMasterRowGeneration ? { platforms: targetPlatforms as string[] } : {}),
        cellMode: effectiveCellMode,
        ...(!isSlotLocked ? { method, templateIds } : {}),
        ...(!isSlotLocked && planningMode === 'manual'
          ? {
              assignedGoal,
              templateId: selectedTemplateId,
              formatId: selectedFormatId,
              assetIntent,
              lengthBucket,
            }
          : {}),
        ...(!isSlotLocked && cellMode === 'series' ? { seriesCount, seriesFlavor } : {}),
        ...(!isSlotLocked && cellMode === 'fresh-variants' ? { freshVariantsCount: freshCount } : {}),
      };
      const researchSelection = isSlotLocked ? undefined : buildResearchSelection(researchScope);

      const res = await client.generatePosts({
        projectId, userId,
        authorId,
        scope: sprintId ? 'sprint' : 'topic',
        strategyId,
        sprintId,
        publicationId,
        topicIds: [topic.id],
        platforms: targetPlatforms as string[],
        cellScope: cellScopePayload,
        ...(researchSelection
          ? { researchSelectionByTopic: { [topic.id]: researchSelection } }
          : {}),
        applyMode,
        planningMode: effectivePlanningMode,
        userInstruction: userInstruction.trim().slice(0, isSlotLocked ? SLOT_INSTRUCTION_MAX : undefined) || undefined,
        skipAssetPlanning: assetMode === 'text_only',
        generateAssetsImmediately: assetMode === 'plan_and_generate',
        // Phase 3.7 — content phase model overrides via the existing
        // `models.platformPost` channel; orchestrator routes provider
        // automatically via getProviderForModel().
        models: { platformPost: model },
      });
      if (!res.success) throw new Error(res.error ?? 'Submission failed');
      // Phase 3 follow-up — pass taskId back so PostsGrid can keep the
      // `<CellTaskBadge>` rendered until the task hits a terminal status.
      onSubmitting?.(false, res.taskId);
      onClose();
      return;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
      onSubmitting?.(false);
    }
  };

  // Total post count preview per mode — surfaces the "1 cell = N posts"
  // math without making the user calculate.
  const totalPosts = useMemo(() => {
    switch (cellMode) {
      case 'variants': return Math.max(1, topicTemplateIds.length);
      case 'series':   return Math.max(1, seriesCount);
      case 'fresh-variants': return Math.max(1, freshCount);
      default: return 1;
    }
  }, [cellMode, topicTemplateIds, seriesCount, freshCount]);
  const bundlePostCount = isMasterRowGeneration && method === 'cope'
    && !isSlotLocked
    ? targetPlatforms.length + 1
    : totalPosts;

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={`${isSlotLocked ? 'Generate slot post' : 'Generate post'} — ${titleSuffix}`}
      maxWidth="sm"
      fullWidth
      data-testid="per-cell-generate-dialog"
      primaryAction={{
        label: submitting
          ? 'Generating…'
          : `Generate ${bundlePostCount} ${bundlePostCount === 1 ? 'post' : 'posts'}`,
        onClick: submit,
        loading: submitting,
        disabled: submitting || !canSubmit,
        'data-testid': 'per-cell-generate-submit',
      }}
    >
      <Stack spacing={2} sx={{ py: 1 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={isMasterRowGeneration
              ? `platforms: ${targetPlatforms.join(', ')}`
              : `platform: ${platform}`}
            size="small"
          />
          {aspectLabel && (
            <Chip label={`aspect: ${aspectLabel}`} size="small" variant="outlined" />
          )}
          {topicTemplateIds.length > 0 && (
            <Chip
              label={`${topicTemplateIds.length} template${topicTemplateIds.length === 1 ? '' : 's'} available`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>

        {isSlotLocked && (
          <Alert severity="info" data-testid={CONTENT_PLAN.PER_CELL_SLOT_LOCK_SUMMARY}>
            Slot assignment locks topic, aspect, platform, sprint goal, reader targeting, template, and format.
            {slotAssignment?.assignedGoal ? ` Goal: ${slotAssignment.assignedGoal}.` : ''}
            {slotAssignment?.templateId ? ` Template: ${slotAssignment.templateId}.` : ''}
            {slotAssignment?.formatId ? ` Format: ${slotAssignment.formatId}.` : ''}
          </Alert>
        )}

        {/* ── MODEL ────────────────────────────────────────────── */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            MODEL
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <AvailableModelSelector
              projectId={projectId}
              value={model}
              onChange={(m) => setModel(m)}
              providers={['google', 'anthropic', 'openai']}
              label="AI model"
              size="small"
            />
            {model === projectDefaultModel.value ? (
              <Chip
                label="project default"
                size="small"
                color="primary"
                variant="outlined"
                data-testid="per-cell-model-is-default"
              />
            ) : !isSlotLocked ? (
              <Button
                size="small"
                variant="text"
                disabled={savingDefault}
                onClick={async () => {
                  setSavingDefault(true);
                  try {
                    await projectDefaultModel.setDefault(model);
                  } catch (e) {
                    setError(`Couldn't set project default: ${(e as Error).message}`);
                  } finally {
                    setSavingDefault(false);
                  }
                }}
                data-testid="per-cell-model-set-default"
              >
                {savingDefault ? 'Saving…' : 'Set as project default'}
              </Button>
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Project default applies wherever the model is selectable. Override
            here for this run only.
          </Typography>
        </Box>

        {/* ── METHOD ───────────────────────────────────────────── */}
        {!isSlotLocked && (
        <Box>
          <Typography variant="caption" color="text.secondary">PLANNING</Typography>
          <RadioGroup
            value={planningMode}
            onChange={(_, v) => setPlanningMode(v as PlanningMode)}
          >
            <FormControlLabel
              value="auto"
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-planning-auto' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Box>
                  <Typography variant="body2">Auto planner</Typography>
                  <Typography variant="caption" color="text.secondary">
                    AI selects method, goal, template, format, length, and asset intent before writing.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="manual"
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-planning-manual' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Box>
                  <Typography variant="body2">Manual plan</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Use the exact method, goal, template, format, and asset intent selected below.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>
        )}

        {!isSlotLocked && planningMode === 'manual' && (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">MANUAL PLAN</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel id="per-cell-goal-label">Goal</InputLabel>
                <Select
                  labelId="per-cell-goal-label"
                  label="Goal"
                  value={assignedGoal}
                  onChange={(e) => {
                    const nextGoal = e.target.value as PostGoal;
                    setAssignedGoal(nextGoal);
                    const nextTemplate = pickTemplateForGoal(nextGoal, selectedFormat, Boolean(selectedFormat));
                    if (nextTemplate?.id) {
                      setSelectedTemplateId(nextTemplate.id);
                      applyTemplateDefaults(nextTemplate.id);
                    }
                  }}
                  data-testid="per-cell-goal-select"
                >
                  {POST_GOALS.map((goal) => (
                    <MenuItem key={goal} value={goal}>{goal}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="per-cell-length-label">Length</InputLabel>
                <Select
                  labelId="per-cell-length-label"
                  label="Length"
                  value={lengthBucket}
                  onChange={(e) => setLengthBucket(e.target.value as 'short' | 'medium' | 'long')}
                  data-testid="per-cell-length-select"
                >
                  <MenuItem value="short">short</MenuItem>
                  <MenuItem value="medium">medium</MenuItem>
                  <MenuItem value="long">long</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <FormControl size="small" fullWidth required>
              <InputLabel id="per-cell-template-label">Template</InputLabel>
              <Select
                labelId="per-cell-template-label"
                label="Template"
                value={selectedTemplateId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedTemplateId(nextId);
                  applyTemplateDefaults(nextId);
                  const nextTemplate = availableTemplates.find((template) => template.id === nextId) ?? null;
                  if (nextTemplate && selectedFormat && !isTemplateCompatibleWithFormat(nextTemplate, selectedFormat)) {
                    const nextFormat = pickBestCompatibleFormatForTemplate(nextTemplate, platformFormats);
                    if (nextFormat?.id) setSelectedFormatId(String(nextFormat.id));
                  }
                }}
                data-testid="per-cell-template-select"
              >
                {availableTemplates.map((template) => (
                  <MenuItem
                    key={template.id}
                    value={template.id}
                    disabled={Boolean(selectedFormat) && !isTemplateCompatibleWithFormat(template, selectedFormat)}
                  >
                    {template.name ?? template.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth required>
              <InputLabel id="per-cell-format-label">Format</InputLabel>
              <Select
                labelId="per-cell-format-label"
                label="Format"
                value={selectedFormatId}
                onChange={(e) => {
                  const nextFormatId = e.target.value;
                  setSelectedFormatId(nextFormatId);
                  const nextFormat = platformFormats.find((format) => format.id === nextFormatId) ?? null;
                  if (selectedTemplate && nextFormat && !isTemplateCompatibleWithFormat(selectedTemplate, nextFormat)) {
                    const nextTemplate = pickTemplateForGoal(assignedGoal, nextFormat, true);
                    if (nextTemplate?.id) {
                      setSelectedTemplateId(nextTemplate.id);
                      applyTemplateDefaults(nextTemplate.id);
                    }
                  }
                }}
                data-testid="per-cell-format-select"
              >
                {platformFormats.map((format) => (
                  <MenuItem key={format.id} value={format.id}>
                    {format.name ?? format.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="per-cell-asset-intent-label">Asset intent</InputLabel>
              <Select
                labelId="per-cell-asset-intent-label"
                label="Asset intent"
                value={assetIntent}
                onChange={(e) => setAssetIntent(e.target.value as AssetIntent)}
                data-testid="per-cell-asset-intent-select"
              >
                {ASSET_INTENTS.map((intent) => (
                  <MenuItem key={intent} value={intent}>{intent}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {!manualPlanComplete && (
              <Alert severity="warning">
                {selectedFormat && templatesCompatibleWithSelectedFormat.length === 0
                  ? 'No selected topic template is compatible with this format. Pick a compatible template in the Topic Templates tab or switch back to Auto planner.'
                  : selectedTemplate && selectedFormat && !selectedFormatCompatible
                  ? 'The selected template is not compatible with the selected format.'
                  : 'Select a compatible goal, template, format, and asset intent before generating.'}
              </Alert>
            )}
          </Stack>
        )}

        {/* ── METHOD ───────────────────────────────────────────── */}
        {!isSlotLocked && (
        <Box>
          <Typography variant="caption" color="text.secondary">METHOD</Typography>
          <RadioGroup
            value={method}
            onChange={(_, v) => setMethod(v as 'cope' | 'direct')}
          >
            <FormControlLabel
              value="cope"
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-method-cope' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Box>
                  <Typography variant="body2">With master post (cope)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reuses or creates a platform-agnostic master, then adapts it for {platform}.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="direct"
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-method-direct' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Box>
                  <Typography variant="body2">Direct (no master)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Single AI call straight to {platform}. No reuse with other platforms.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>
        )}

        {/* ── MODE ─────────────────────────────────────────────── */}
        {!isSlotLocked && (
        <Box>
          <Typography variant="caption" color="text.secondary">MODE</Typography>
          <RadioGroup
            value={cellMode}
            onChange={(_, v) => setCellMode(v as CellMode)}
          >
            <FormControlLabel
              value="single"
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-mode-single' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Typography variant="body2">Single post</Typography>
              }
            />
            <FormControlLabel
              value="variants"
              disabled={!variantsEligible}
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-mode-variants' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Box>
                  <Typography variant="body2">
                    Template variants — {topicTemplateIds.length || 0} alternatives
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {variantsEligible
                      ? 'One post per template selected in the Templates tab.'
                      : 'Pick 2+ templates in the Templates tab to enable.'}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="fresh-variants"
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-mode-fresh-variants' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Box>
                  <Typography variant="body2">Fresh variants — same template, distinct seeds</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Multiple AI passes of the same template for A/B comparison.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="series"
              control={<Radio size="small" inputProps={{ 'data-testid': 'per-cell-mode-series' } as React.InputHTMLAttributes<HTMLInputElement>} />}
              label={
                <Typography variant="body2">Series — narrative sequence</Typography>
              }
            />
          </RadioGroup>

          {cellMode === 'series' && (
            <Stack spacing={1} sx={{ mt: 1, pl: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Parts in sequence: <strong>{seriesCount}</strong>
                </Typography>
                <Slider
                  value={seriesCount}
                  min={2}
                  max={10}
                  step={1}
                  marks
                  onChange={(_, v) => setSeriesCount(v as number)}
                  size="small"
                  sx={{ maxWidth: 320 }}
                  data-testid="per-cell-series-count-slider"
                  slotProps={{
                    input: { 'data-testid': 'per-cell-series-count-input' } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                />
              </Box>
              <FormControl size="small" sx={{ maxWidth: 240 }}>
                <InputLabel id="per-cell-flavor-label">Flavor</InputLabel>
                <Select
                  labelId="per-cell-flavor-label"
                  label="Flavor"
                  value={seriesFlavor}
                  onChange={(e) => setSeriesFlavor(e.target.value as SeriesFlavor)}
                  data-testid="per-cell-series-flavor-select"
                  inputProps={{ 'data-testid': 'per-cell-series-flavor-input' }}
                >
                  <MenuItem value="continuation" data-testid="per-cell-series-flavor-continuation">continuation</MenuItem>
                  <MenuItem value="counter_point" data-testid="per-cell-series-flavor-counter_point">counter_point</MenuItem>
                  <MenuItem value="how_to_sequence" data-testid="per-cell-series-flavor-how_to_sequence">how_to_sequence</MenuItem>
                  <MenuItem value="story_arc" data-testid="per-cell-series-flavor-story_arc">story_arc</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {cellMode === 'fresh-variants' && (
            <Box sx={{ mt: 1, pl: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Variants: <strong>{freshCount}</strong>
              </Typography>
              <Slider
                value={freshCount}
                min={2}
                max={5}
                step={1}
                marks
                onChange={(_, v) => setFreshCount(v as number)}
                size="small"
                sx={{ maxWidth: 240 }}
                data-testid="per-cell-fresh-count-slider"
                slotProps={{
                  input: { 'data-testid': 'per-cell-fresh-count-input' } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            </Box>
          )}
        </Box>
        )}

        {/* ── ASSETS ──────────────────────────────────────────── */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            ASSETS
          </Typography>
          <ToggleButtonGroup
            value={assetMode}
            exclusive
            size="small"
            onChange={(_, v) => {
              if (!v) return;
              const prev = assetMode;
              setAssetMode(v as AssetMode);
              setAssetModeUserTouched(true);
              import('../../telemetry/designBriefEvents')
                .then((m) => m.emitAssetModeChanged({ source: 'ui', newValue: v as AssetMode, oldValue: prev }))
                .catch(() => undefined);
            }}
            data-testid="per-cell-asset-mode-toggle"
          >
            <ToggleButton value="plan_and_generate" data-testid="per-cell-asset-mode-plan_and_generate">Plan + generate</ToggleButton>
            <ToggleButton value="plan_only" data-testid="per-cell-asset-mode-plan_only">Plan only</ToggleButton>
            <ToggleButton value="text_only" data-testid="per-cell-asset-mode-text_only">Text only</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <FormControl fullWidth>
          <TextField
            label="Instruction (optional)"
            placeholder='e.g. "Lean into the contrarian angle"'
            size="small"
            multiline
            minRows={2}
            value={userInstruction}
            onChange={(e) => setUserInstruction(isSlotLocked
              ? e.target.value.slice(0, SLOT_INSTRUCTION_MAX)
              : e.target.value)}
            helperText={isSlotLocked
              ? `Creative guidance only; slot locks still win. ${userInstruction.length}/${SLOT_INSTRUCTION_MAX}`
              : undefined}
            inputProps={isSlotLocked ? { maxLength: SLOT_INSTRUCTION_MAX } : undefined}
          />
        </FormControl>

        {!isSlotLocked && topicRuns.length > 0 && (
          <Box>
            <ResearchScopeSelector
              runs={topicRuns}
              state={researchScope}
              onChange={setResearchScope}
              dense
            />
          </Box>
        )}

        {!isSlotLocked && (
        <Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={draftReview}
                onChange={(e) => setDraftReview(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Save as draft for review</Typography>
                <Typography variant="caption" color="text.secondary">
                  Default: post auto-applies to {sprintId ? 'sprint' : 'topic'} immediately.
                  Toggle to land in the legacy review queue first.
                </Typography>
              </Box>
            }
          />
        </Box>
        )}
      </Stack>
    </SDialog>
  );
};

export default PerCellGenerateDialog;
